import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Checkbox,
	Divider,
	FormControlLabel,
	Grid,
	Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { FC } from 'react';
import styled from 'styled-components';
import { useAppSelector } from '@src/store/hooks';
import { adminUsers } from '@src/helpers/Permission';

const StyledAccordion = styled(Accordion)`
	position: static !important;
	border-radius: 10px;
	border: 1.5px solid var(--gray-g-10, #f1f1f1);
	background: #fff;
	box-shadow: none !important;
	margin-bottom: 10px;

	& .MuiAccordionSummary-root {
		width: 100%;
		border-bottom: 1.5px solid transparent;
		&.Mui-expanded {
			border-bottom-color: #f1f1f1;
		}
	}
	& .MuiAccordionSummary-content {
		display: flex;
		align-items: center;
		gap: 10px;
	}
`;

const StyledCount = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 24px;
	height: 24px;
	padding: 4px;
	background: #ffbe00;
	border-radius: 50%;
	color: var(--default-text-color);
	font-family: Poppins;
	font-size: 12px;
	font-weight: 500;
	line-height: 15.6px;
	@media screen and (max-width: 1024px) {
		margin-left: 8px;
	}
`;

const FeaturePermissions = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	p {
		color: var(--gray-g-500, #979797);
		font-family: Poppins;
		font-weight: 500;
		line-height: 23.4px;
		text-transform: capitalize;
		letter-spacing: 0.2px;
		font-size: 16px;
	}
`;

const CustomCheckbox = styled(Checkbox)({
	// color: 'var(--color-font-grey) !important',
	// '&.Mui-checked': {
	// 	borderRadius: '8px !important',
	// },
});

const CustomLabel = styled('span')({
	fontSize: '12px',
	fontWeight: 600,
	lineHeight: '18px',
	letterSpacing: '0.20px',
});

const checkboxStyles = {
	color: 'var(--color-font-grey)',
	fontSize: '14px',
	fontWeight: 600,
	lineHeight: '18px',
};

export interface Permission {
	name: string;
	key: string;
	checked: boolean;
}

export interface Category {
	category: string;
	permissions: Permission[];
}

export interface Group {
	group: string;
	list: Category[];
}

interface Props {
	accessLevel: string;
	data: Group[];
	setData: (data: Group[]) => void;
}

const PermissionContainer: FC<Props> = ({ accessLevel, data, setData }) => {
	const currentUser: any = useAppSelector((state) => state?.currentUser?.value);
	const isAdmin = accessLevel === '2';

	const handleCheckboxChange = (
		groupIndex: number,
		categoryIndex: number,
		permissionIndex: number
	) => {
		const updatedData = [...data];
		updatedData[groupIndex].list[categoryIndex].permissions[permissionIndex].checked =
			!updatedData?.[groupIndex]?.list[categoryIndex]?.permissions[permissionIndex]?.checked;
		setData(updatedData);
	};

	const handleCategorySelectAll = (groupIndex: number, categoryIndex: number) => {
		const updatedData = [...data];
		const permissions = updatedData[groupIndex].list[categoryIndex].permissions;
		const allChecked = permissions?.every((permission) => permission?.checked);

		permissions.forEach((permission) => {
			permission.checked = !allChecked;
		});

		setData(updatedData);
	};

	const mapGroup = (group: Group, groupIndex: number) => {
		if ((accessLevel === '1' && group?.group !== 'Organization') || accessLevel === '2') {
			return (
				<StyledAccordion key={`group_${groupIndex}`} defaultExpanded={[0, 1].includes(groupIndex)}>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls={`panel${groupIndex + 1}-content`}
						id={`panel${groupIndex + 1}-header`}
					>
						<Typography variant="h4" sx={{ letterSpacing: '0.20px' }}>
							{group?.group}
						</Typography>
						<StyledCount>
							{group?.list?.reduce((total, category) => total + category?.permissions?.length, 0)}
						</StyledCount>
					</AccordionSummary>
					<AccordionDetails
						sx={{
							display: 'grid',
							gridTemplateColumns: 'repeat(4, 1fr)',
							gap: '10px',
							height: 'auto',
							overflowY: 'auto',
							margin: '10px 10px 0 10px',
							'@media screen and (width <= 1300px)': {
								gridTemplateColumns: 'repeat(3, 1fr)',
							},
							'@media screen and (width <= 800px)': {
								gridTemplateColumns: 'repeat(2, 1fr)',
							},
							'@media screen and (width <= 600px)': {
								gridTemplateColumns: 'repeat(1, 1fr)',
							},
						}}
					>
						{group?.list?.map((category, categoryIndex) => (
							<FeaturePermissions key={`category_${categoryIndex}`}>
								<p>{category?.category}</p>
								<Grid container direction="column">
									<Grid item key={`select_all_${categoryIndex}`}>
										<FormControlLabel
											control={
												<Checkbox
													checked={
														isAdmin || category?.permissions?.every((perm) => perm.checked) || false
													}
													onChange={() => handleCategorySelectAll(groupIndex, categoryIndex)}
													disabled={isAdmin}
												/>
											}
											label={<CustomLabel>Select All</CustomLabel>}
										/>
									</Grid>
									<Divider />
									{category?.permissions?.map((permission, permissionIndex) => (
										<Grid item key={`permission_${permissionIndex}`}>
											<FormControlLabel
												control={
													<CustomCheckbox
														checked={isAdmin || permission?.checked || false}
														onChange={() =>
															handleCheckboxChange(groupIndex, categoryIndex, permissionIndex)
														}
														disabled={isAdmin}
													/>
												}
												label={<CustomLabel>{permission?.name}</CustomLabel>}
												labelPlacement="end"
											/>
										</Grid>
									))}
								</Grid>
							</FeaturePermissions>
						))}
					</AccordionDetails>
				</StyledAccordion>
			);
		}
	};
	return <div>{data?.map((group, groupIndex) => mapGroup(group, groupIndex))}</div>;
};

export default PermissionContainer;
